/**
 * LES GRILLES TARIFAIRES — 15/09/2026, lot 1 de l'Étape 20.
 *
 * Demandé par Mohamed : « j'aimerais que l'on ajoute le mécanisme d'indexation
 * gasoil et donc de grille tarifaire ». Maquette `docs/maquettes/tarifs.html`,
 * écrans 1, 2, 2 bis et 2 ter.
 *
 *
 * CE QUE CET ÉCRAN NE FAIT PAS ENCORE
 * ---------------------------------------------------------------------------
 * Il ne compare aucun prix d'ordre au tarif (lot 2) et ne calcule aucune
 * surcharge (lot 4). L'indice de référence et la part gasoil se saisissent et
 * s'affichent — rien de plus, et l'écran le dit plutôt que de le laisser
 * croire.
 *
 *
 * DEUX VUES, PAS TROIS
 * ---------------------------------------------------------------------------
 * La liste, et une grille ouverte. La maquette dessine la grille comme un
 * écran plein et non comme un volet : elle porte un tableau de lignes qui
 * s'allonge, et un volet de 400 px le rendrait illisible dès Cemex.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ajouterUneLigneTarif,
  creerUneGrille,
  dupliquerUneGrille,
  lireDepartements,
  lireLaGrille,
  lireLesGrilles,
  lireListe,
  lireVehicules,
  modifierUneGrille,
  retirerUneLigneTarif,
  supprimerUneGrille,
  type Departement,
  type EchecApi,
  type Grille,
  type LigneListe,
  type LigneTarif,
  type Vehicule,
} from './api.js';
import { ANNUAIRE_SITES } from './sites.js';
import { enFrancais } from './ot.js';
import { ANNUAIRE_CLIENTS } from './clients.js';

/**
 * Ce que l'API a refusé, dit en français.
 *
 * `Partial` ET NON `Record` complet : `EchecApi` porte les refus de toute
 * l'application — ceux du planning, de l'emballage, de Pennylane — dont aucun
 * ne peut venir d'ici. Les énumérer pour satisfaire le typage donnerait vingt
 * phrases mortes ; celles qui manquent tombent sur le repli ci-dessous.
 */
const REFUS: Partial<Readonly<Record<EchecApi, string>>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette grille n’existe plus. Revenez à la liste.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris.',
  invalide: 'La saisie est incomplète ou hors des valeurs attendues.',
  conflit:
    'Ce client a déjà une grille ouverte à ce départ, ou une ligne couvre déjà cette destination.',
  encore_actif: 'Clôturez-la d’abord.',
  encore_rattache: 'Ce client, ce site ou ce matériel n’existe pas.',
};

const REFUS_INCONNU = 'Stafa a refusé cette saisie. Réessayez ou rechargez l’écran.';

const UNITES: readonly { readonly cle: string; readonly nom: string }[] = [
  { cle: 'palette', nom: 'à la palette' },
  { cle: 'kilometre', nom: 'au kilomètre' },
  { cle: 'trajet', nom: 'au trajet' },
];

const STYLES: readonly { readonly cle: string; readonly nom: string }[] = [
  { cle: 'site', nom: 'site' },
  { cle: 'departement', nom: 'département' },
];

function uniteDite(cle: string): string {
  return UNITES.find((u) => u.cle === cle)?.nom ?? cle;
}

function styleDit(grille: Grille): string {
  const nom = (cle: string) => STYLES.find((s) => s.cle === cle)?.nom ?? cle;
  return `${nom(grille.style_origine)} → ${nom(grille.style_destination)}`;
}

function sensDit(sens: string): string {
  return sens === 'aller_retour' ? 'aller et retour' : 'aller seul';
}

/** Le départ d'une grille, ou « toutes origines » quand elle n'en impose aucun. */
function departDit(grille: Grille): string {
  if (grille.origine_site !== null) return grille.origine_site;
  if (grille.origine_departement !== null) return `dépt ${grille.origine_departement}`;
  return 'toutes origines';
}

function destinationDite(ligne: LigneTarif): string {
  if (ligne.destination_site !== null) return ligne.destination_site;
  if (ligne.destination_departement !== null) {
    return `${ligne.destination_departement} · ${ligne.destination_departement_nom ?? ''}`.trim();
  }
  return 'toutes';
}

/**
 * LA TRANCHE, DITE COMME ON LA LIT. Bornes basses incluses, hautes exclues —
 * « jusqu'à 15 t » puis « de 15 à 25 t » puis « au-delà de 25 t ».
 */
function trancheDite(ligne: LigneTarif): string {
  const paires: readonly [number | null, number | null, (n: number) => string][] = [
    [ligne.poids_min_kg, ligne.poids_max_kg, (n) => `${n / 1000} t`],
    [ligne.km_min, ligne.km_max, (n) => `${n} km`],
    [ligne.palettes_min, ligne.palettes_max, (n) => `${n} pal`],
  ];
  for (const [min, max, dire] of paires) {
    if (min === null && max === null) continue;
    if (min === null || min === 0) return `jusqu’à ${dire(max as number)}`;
    if (max === null) return `au-delà de ${dire(min)}`;
    // UNE TRANCHE D'UNE SEULE VALEUR SE DIT PAR CETTE VALEUR, et non « de 3 à
    // 4 » : la borne haute est exclue, donc [3,4) est « 3 palettes ». C'est la
    // forme la plus courante d'une grille palette — un prix par nombre.
    if (max === min + 1) return dire(min);
    return `de ${dire(min)} à ${dire(max as number)}`;
  }
  return 'toutes';
}

/**
 * L'INDICE, ÉCRIT COMME IL A ÉTÉ SAISI — 15/09/2026.
 *
 * NÉCESSAIRE À CAUSE DU CHANGEMENT DU JOUR MÊME : la colonne est passée en
 * `numeric(10, 4)`, et PostgreSQL rend désormais « 118.6000 » là où il rendait
 * « 118.60 ». Afficher la chaîne brute ferait lire à Mohamed quatre décimales
 * qu'il n'a pas tapées — une dégradation introduite par le correctif, donc
 * réparée avec lui.
 *
 * DEUX DÉCIMALES AU MINIMUM, QUATRE AU MAXIMUM : « 118,60 » reste « 118,60 »,
 * et « 1,2875 » reste « 1,2875 ». Rien n'est arrondi, seuls les zéros de
 * remplissage tombent.
 */
function enIndice(valeur: string): string {
  const n = Number(valeur);
  if (!Number.isFinite(n)) return valeur;
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(n);
}

function enEuros(montant: string): string {
  const n = Number(montant);
  if (!Number.isFinite(n)) return montant;
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)} €`;
}

/**
 * CE QUE LE MONTANT D'UNE LIGNE REPRÉSENTE — corrigé le 15/09/2026.
 *
 * Cette fonction écrivait « / palette » : c'était FAUX, et Mohamed l'a relevé
 * le jour même. « Un prix à la palette n'est pas juste un prix × le nombre de
 * palettes mais un prix différent pour chaque nombre de palettes. » Un
 * transport de 6 palettes aurait valu 6 × 71 = 426 € là où sa grille dit 195.
 *
 * SEUL LE KILOMÈTRE SE MULTIPLIE. La palette et le trajet donnent un TOTAL.
 */
function parUnite(unite: string): string {
  return unite === 'kilometre' ? '/ km' : '';
}

interface SaisieGrille {
  readonly clientId: string;
  readonly nom: string;
  readonly unite: string;
  readonly styleOrigine: string;
  readonly styleDestination: string;
  readonly origineSiteId: string;
  readonly origineDepartement: string;
  readonly du: string;
  readonly indice: string;
  readonly part: string;
}

const SAISIE_VIDE: SaisieGrille = {
  clientId: '',
  nom: '',
  unite: 'palette',
  styleOrigine: 'site',
  styleDestination: 'departement',
  origineSiteId: '',
  origineDepartement: '',
  du: new Date().toISOString().slice(0, 10),
  indice: '',
  part: '',
};

interface SaisieLigne {
  readonly destinationSiteId: string;
  readonly destinationDepartement: string;
  readonly sens: string;
  readonly vehiculeCode: string;
  readonly prix: string;
  readonly min: string;
  readonly max: string;
}

const LIGNE_VIDE: SaisieLigne = {
  destinationSiteId: '',
  destinationDepartement: '',
  sens: 'aller',
  vehiculeCode: '',
  prix: '',
  min: '',
  max: '',
};

/**
 * UN NOMBRE TAPÉ À LA FRANÇAISE, NETTOYÉ DE CE QU'ON TAPE VRAIMENT.
 *
 * CONSTATÉ PAR MOHAMED le 15/09/2026 : « indice_reference : montant positif,
 * deux décimales au plus ». L'écran envoyait tel quel ce qu'il avait sous la
 * main, et l'API — dont la règle est « chiffres, deux décimales, rien
 * d'autre » — refusait sans dire ce qu'elle avait reçu.
 *
 * CE QU'ON TAPE DANS UN CHAMP DE MONTANT, en vrai : « 118,60 », mais aussi
 * « 118.60 », « 1 186,00 » avec une espace de groupement, « 118,60 € », « 32 % »,
 * et l'espace INSÉCABLE que Windows et le presse-papiers glissent sans qu'on la
 * voie. Rien de tout cela n'est une faute de l'utilisateur.
 *
 * CE QU'ON NE NETTOIE PAS : une décimale de trop. « 118,600 » sur un prix n'est
 * pas une erreur de frappe, c'est un chiffre qu'on ne sait pas ranger dans la
 * colonne visée — l'arrondir en silence changerait un montant. On refuse, et on
 * le dit.
 *
 * COMBIEN DE DÉCIMALES DÉPEND DU CHAMP — corrigé le 15/09/2026. La première
 * rédaction en imposait deux partout, et refusait donc « 1,2875 », qui est
 * l'indice de Mohamed. Chaque appelant dit maintenant ce que sa colonne
 * accepte.
 */
export function enNombre(saisi: string): string {
  return saisi
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(/[€%]/g, '')
    .replace(',', '.')
    .trim();
}

/**
 * Ce champ est-il un montant acceptable ? Rend la phrase du refus, ou `null`.
 *
 * LE CONTRÔLE EST ICI EN PLUS DE L'API, et non à sa place : l'API reste ce qui
 * décide. Mais elle parle en noms de colonnes — « indice_reference » — et
 * l'écran, lui, connaît l'étiquette que l'utilisateur a sous les yeux.
 */
export function fauteDeMontant(
  saisi: string,
  etiquette: string,
  /** Décimales acceptées par la colonne visée. DEUX par défaut — un prix. */
  decimales: 2 | 4 = 2,
): string | null {
  const propre = enNombre(saisi);
  if (propre === '') return null;
  if (!new RegExp(`^[0-9]{1,10}(\\.[0-9]{1,${String(decimales)}})?$`).test(propre)) {
    const exemple = decimales === 4 ? '1,2875' : '118,60';
    const combien = decimales === 4 ? 'quatre' : 'deux';
    return `${etiquette} : un nombre comme ${exemple} — ${combien} décimales au plus, et rien d’autre. Reçu : « ${saisi.trim()} ».`;
  }
  return null;
}

function enEntier(saisi: string): number | null {
  if (saisi.trim() === '') return null;
  const n = Number(saisi.trim());
  return Number.isInteger(n) && n >= 0 ? n : null;
}

// ---------------------------------------------------------------------------
// LA BOÎTE D'OUVERTURE D'UNE GRILLE
// ---------------------------------------------------------------------------

/**
 * L'UNITÉ ET LES DEUX STYLES SE CHOISISSENT ICI, ET PLUS JAMAIS. L'aide sous
 * les champs le dit avant le clic, plutôt que de laisser l'API le dire après.
 */
export function BoiteGrille({
  saisie,
  clients,
  sites,
  departements,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: SaisieGrille;
  readonly clients: readonly LigneListe[];
  readonly sites: readonly LigneListe[];
  readonly departements: readonly Departement[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: keyof SaisieGrille, valeur: string) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Nouvelle grille</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="grille-client">
            Client
          </label>
          <select
            id="grille-client"
            className="champ-boite"
            value={saisie.clientId}
            disabled={envoi}
            onChange={(e) => quandChamp('clientId', e.target.value)}
          >
            <option value="">—</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom}
              </option>
            ))}
          </select>

          <label className="etiquette-boite" htmlFor="grille-nom">
            Nom de la grille
          </label>
          <input
            id="grille-nom"
            className="champ-boite"
            type="text"
            placeholder="Départ Poupry"
            value={saisie.nom}
            disabled={envoi}
            onChange={(e) => quandChamp('nom', e.target.value)}
          />
          <p className="aide-boite">
            C’est lui qui distingue deux grilles d’un même client — VPK en a
            deux, « départ Lisieux » et « départ Alizay ».
          </p>

          <div className="paire-boite">
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="grille-unite">
                Unité de prix
              </label>
              <select
                id="grille-unite"
                className="champ-boite"
                value={saisie.unite}
                disabled={envoi}
                onChange={(e) => quandChamp('unite', e.target.value)}
              >
                {UNITES.map((u) => (
                  <option key={u.cle} value={u.cle}>
                    {u.nom}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="grille-du">
                Valable du
              </label>
              <input
                id="grille-du"
                className="champ-boite"
                type="date"
                value={saisie.du}
                disabled={envoi}
                onChange={(e) => quandChamp('du', e.target.value)}
              />
            </div>
          </div>

          <div className="paire-boite">
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="grille-style-o">
                Le départ s’exprime en
              </label>
              <select
                id="grille-style-o"
                className="champ-boite"
                value={saisie.styleOrigine}
                disabled={envoi}
                onChange={(e) => quandChamp('styleOrigine', e.target.value)}
              >
                {STYLES.map((s) => (
                  <option key={s.cle} value={s.cle}>
                    {s.nom}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="grille-style-d">
                Les destinations en
              </label>
              <select
                id="grille-style-d"
                className="champ-boite"
                value={saisie.styleDestination}
                disabled={envoi}
                onChange={(e) => quandChamp('styleDestination', e.target.value)}
              >
                {STYLES.map((s) => (
                  <option key={s.cle} value={s.cle}>
                    {s.nom}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="aide-boite">
            <b>L’unité et les deux styles se posent maintenant et ne se
            modifieront plus.</b> Changer l’unité d’une grille vivante
            transformerait « 71 € la palette » en « 71 € le kilomètre » sur
            toutes ses lignes d’un coup. Pour en changer : clôturer, dupliquer.
          </p>

          <label className="etiquette-boite" htmlFor="grille-depart">
            Départ (facultatif)
          </label>
          {saisie.styleOrigine === 'site' ? (
            <select
              id="grille-depart"
              className="champ-boite"
              value={saisie.origineSiteId}
              disabled={envoi}
              onChange={(e) => quandChamp('origineSiteId', e.target.value)}
            >
              <option value="">tous les départs</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom}
                </option>
              ))}
            </select>
          ) : (
            <select
              id="grille-depart"
              className="champ-boite"
              value={saisie.origineDepartement}
              disabled={envoi}
              onChange={(e) => quandChamp('origineDepartement', e.target.value)}
            >
              <option value="">tous les départs</option>
              {departements.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.code} · {d.nom}
                </option>
              ))}
            </select>
          )}

          <div className="paire-boite">
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="grille-indice">
                Indice de référence
              </label>
              <input
                id="grille-indice"
                className="champ-boite"
                type="text"
                inputMode="decimal"
                placeholder="1,2875"
                value={saisie.indice}
                disabled={envoi}
                onChange={(e) => quandChamp('indice', e.target.value)}
              />
            </div>
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="grille-part">
                Part gasoil (%)
              </label>
              <input
                id="grille-part"
                className="champ-boite"
                type="text"
                inputMode="decimal"
                placeholder="32"
                value={saisie.part}
                disabled={envoi}
                onChange={(e) => quandChamp('part', e.target.value)}
              />
            </div>
          </div>
          <p className="aide-boite">
            Les deux ensemble, ou aucun des deux : un indice sans part ne
            calcule rien. Laissez vides pour une grille sans indexation —
            c’est le cas de Leroy et de Flash. L’indice se saisit{' '}
            <b>jusqu’à quatre décimales</b> et n’est jamais arrondi ; la part
            gasoil est un pourcentage, à deux décimales.{' '}
            <b>Aucune surcharge n’est encore calculée à partir de ces deux
            nombres</b> : ils se saisissent aujourd’hui, ils serviront au lot 4.
          </p>

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              // LA DATE COMPTE AUTANT QUE LE CLIENT ET LE NOM. Sans elle
              // l'API refuse, et jusqu'au 15/09 elle refusait avec une phrase
              // que l'écran remplaçait par « la saisie est incomplète » —
              // devant neuf champs.
              disabled={
                envoi ||
                saisie.clientId === '' ||
                saisie.nom.trim() === '' ||
                saisie.du.trim() === ''
              }
            >
              {envoi ? 'Ouverture…' : 'Ouvrir la grille'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LA LISTE
// ---------------------------------------------------------------------------

export function VueGrilles({
  grilles,
  cloturees,
  chargement,
  quandCloturees,
  quandOuvrir,
  quandNouvelle,
}: {
  readonly grilles: readonly Grille[];
  readonly cloturees: boolean;
  readonly chargement: boolean;
  readonly quandCloturees: (valeur: boolean) => void;
  readonly quandOuvrir: (id: string) => void;
  readonly quandNouvelle: () => void;
}) {
  return (
    <>
      <div className="barre-outils">
        <span className="decompte">
          {chargement
            ? 'Chargement…'
            : `${grilles.length} grille${grilles.length > 1 ? 's' : ''} ${
                cloturees ? 'clôturée' : 'active'
              }${grilles.length > 1 ? 's' : ''}`}
        </span>
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandCloturees(!cloturees)}
        >
          {cloturees ? 'Grilles actives' : 'Grilles clôturées'}
        </button>
        <button className="bouton" type="button" onClick={quandNouvelle}>
          Nouvelle grille
        </button>
      </div>

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Départ</th>
              <th>Style</th>
              <th>Unité de prix</th>
              <th>Lignes</th>
              <th>Indexation</th>
              <th>Validité</th>
            </tr>
          </thead>
          <tbody>
            {grilles.length === 0 && !chargement && (
              <tr>
                <td colSpan={7} className="rien">
                  {cloturees
                    ? 'Aucune grille clôturée.'
                    : 'Aucune grille. Ouvrez la première — c’est elle qui portera la clause gasoil de ce client.'}
                </td>
              </tr>
            )}
            {grilles.map((g) => (
              <tr
                key={g.id}
                className="rangee"
                role="button"
                tabIndex={0}
                onClick={() => quandOuvrir(g.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    quandOuvrir(g.id);
                  }
                }}
              >
                <td>
                  <b>{g.client}</b>
                  <span className="secondaire"> · {g.nom}</span>
                </td>
                <td>{departDit(g)}</td>
                <td className="sourdine">{styleDit(g)}</td>
                <td>{uniteDite(g.unite)}</td>
                <td className="code">{g.lignes}</td>
                <td>
                  {g.indice_reference === null ? (
                    <span className="sourdine">aucune</span>
                  ) : (
                    <span className="code">
                      réf. {enIndice(g.indice_reference)} · {Number(g.part_gazole)} %
                    </span>
                  )}
                </td>
                <td className="code">
                  {enFrancais(g.du) ?? g.du}
                  {g.au === null ? '' : ` → ${enFrancais(g.au) ?? g.au}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// UNE GRILLE OUVERTE
// ---------------------------------------------------------------------------

export function VueGrille({
  grille,
  lignes,
  saisie,
  sites,
  departements,
  vehicules,
  envoi,
  refus,
  message,
  quandRetour,
  quandChamp,
  quandAjouter,
  quandRetirer,
  quandCloturer,
  quandDupliquer,
}: {
  readonly grille: Grille;
  readonly lignes: readonly LigneTarif[];
  readonly saisie: SaisieLigne;
  readonly sites: readonly LigneListe[];
  readonly departements: readonly Departement[];
  readonly vehicules: readonly Vehicule[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly message: string | null;
  readonly quandRetour: () => void;
  readonly quandChamp: (cle: keyof SaisieLigne, valeur: string) => void;
  readonly quandAjouter: () => void;
  readonly quandRetirer: (ligneId: string) => void;
  readonly quandCloturer: () => void;
  readonly quandDupliquer: () => void;
}) {
  const cloturee = grille.au !== null;
  // LES TROIS UNITÉS PORTENT UNE TRANCHE depuis le 15/09 : le poids pour le
  // trajet, le kilométrage pour le kilomètre, LE NOMBRE DE PALETTES pour la
  // palette. C'est la même mécanique, et elle s'affiche dans la même colonne.
  const avecTranche = true;
  const libelleTranche =
    grille.unite === 'trajet'
      ? 'Poids (kg)'
      : grille.unite === 'kilometre'
        ? 'Kilométrage'
        : 'Nombre de palettes';

  return (
    <>
      <div className="barre-outils">
        <button className="bouton bouton-discret" type="button" onClick={quandRetour}>
          ← Les grilles
        </button>
        <span className="decompte">
          <b>{grille.client}</b> · {grille.nom}
        </span>
        <span className={cloturee ? 'etat-liste eteint' : 'etat-liste'}>
          {cloturee
            ? `clôturée le ${enFrancais(grille.au) ?? String(grille.au)}`
            : 'active'}
        </span>
        {!cloturee && (
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={envoi}
            onClick={quandCloturer}
          >
            Clôturer
          </button>
        )}
        <button
          className="bouton bouton-discret"
          type="button"
          disabled={envoi}
          onClick={quandDupliquer}
        >
          Dupliquer
        </button>
      </div>

      <div className="entete-grille">
        <dl className="champs-ot">
          <div className="champ-ot">
            <dt>Départ</dt>
            <dd>{departDit(grille)}</dd>
          </div>
          <div className="champ-ot">
            <dt>Style du trajet</dt>
            <dd className="fige">{styleDit(grille)}</dd>
          </div>
          <div className="champ-ot">
            <dt>Unité de prix</dt>
            <dd className="fige">{uniteDite(grille.unite)}</dd>
          </div>
          <div className="champ-ot">
            <dt>Validité</dt>
            <dd className="code">
              {enFrancais(grille.du) ?? grille.du}
              {grille.au === null
                ? ' → ouverte'
                : ` → ${enFrancais(grille.au) ?? grille.au}`}
            </dd>
          </div>
          <div className="champ-ot">
            <dt>Indexation gasoil</dt>
            <dd>
              {grille.indice_reference === null ? (
                <span className="sourdine">aucune</span>
              ) : (
                <span className="code">
                  référence {enIndice(grille.indice_reference)} · {Number(grille.part_gazole)} %
                </span>
              )}
            </dd>
          </div>
        </dl>
        <p className="sourdine">
          Le style et l’unité sont figés — ils se sont choisis à l’ouverture.
          Pour en changer : clôturer, puis dupliquer.
        </p>
      </div>

      {message !== null && <p className="bandeau-fait">{message}</p>}
      {refus !== null && <p className="bandeau-refus">{refus}</p>}

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th>Destination</th>
              <th>Sens</th>
              <th>Véhicule</th>
              {avecTranche && <th>{libelleTranche}</th>}
              <th className="nombre-tarif">Prix</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 && (
              <tr>
                <td colSpan={avecTranche ? 6 : 5} className="rien">
                  Aucune ligne. Tant que cette grille est vide, elle ne tarife
                  rien.
                </td>
              </tr>
            )}
            {lignes.map((l) => (
              <tr key={l.id}>
                <td>{destinationDite(l)}</td>
                <td className="sourdine">{sensDit(l.sens)}</td>
                <td>
                  {l.vehicule_code === null ? (
                    <span className="sourdine">tous</span>
                  ) : (
                    <b>{l.vehicule ?? l.vehicule_code}</b>
                  )}
                </td>
                {avecTranche && <td className="sourdine">{trancheDite(l)}</td>}
                <td className="nombre-tarif code">
                  {enEuros(l.prix)} <span className="sourdine">{parUnite(grille.unite)}</span>
                </td>
                <td className="actions-liste">
                  <button
                    className="lien-action"
                    type="button"
                    disabled={envoi}
                    onClick={() => quandRetirer(l.id)}
                  >
                    Retirer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* LA SAISIE D'UNE LIGNE EST SOUS LE TABLEAU, PAS DANS UNE BOÎTE : on en
          pose douze à la suite quand on saisit un contrat, et rouvrir une
          fenêtre douze fois est une punition. */}
      <form
        className="ajout-ligne-tarif"
        onSubmit={(e) => {
          e.preventDefault();
          quandAjouter();
        }}
      >
        {grille.style_destination === 'site' ? (
          <select
            className="champ"
            aria-label="Destination"
            value={saisie.destinationSiteId}
            disabled={envoi}
            onChange={(e) => quandChamp('destinationSiteId', e.target.value)}
          >
            <option value="">toutes destinations</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nom}
              </option>
            ))}
          </select>
        ) : (
          <select
            className="champ"
            aria-label="Destination"
            value={saisie.destinationDepartement}
            disabled={envoi}
            onChange={(e) => quandChamp('destinationDepartement', e.target.value)}
          >
            <option value="">toutes destinations</option>
            {departements.map((d) => (
              <option key={d.code} value={d.code}>
                {d.code} · {d.nom}
              </option>
            ))}
          </select>
        )}
        <select
          className="champ"
          aria-label="Sens"
          value={saisie.sens}
          disabled={envoi}
          onChange={(e) => quandChamp('sens', e.target.value)}
        >
          <option value="aller">aller seul</option>
          <option value="aller_retour">aller et retour</option>
        </select>
        <select
          className="champ"
          aria-label="Véhicule"
          value={saisie.vehiculeCode}
          disabled={envoi}
          onChange={(e) => quandChamp('vehiculeCode', e.target.value)}
        >
          <option value="">tous véhicules</option>
          {vehicules.map((v) => (
            <option key={v.code} value={v.code}>
              {v.libelle}
            </option>
          ))}
        </select>
        {avecTranche && (
          <>
            <input
              className="champ court"
              type="text"
              inputMode="numeric"
              aria-label={`${libelleTranche} — de`}
              placeholder="de"
              value={saisie.min}
              disabled={envoi}
              onChange={(e) => quandChamp('min', e.target.value)}
            />
            <input
              className="champ court"
              type="text"
              inputMode="numeric"
              aria-label={`${libelleTranche} — à`}
              placeholder="à (exclu)"
              value={saisie.max}
              disabled={envoi}
              onChange={(e) => quandChamp('max', e.target.value)}
            />
          </>
        )}
        <input
          className="champ court"
          type="text"
          inputMode="decimal"
          aria-label="Prix"
          placeholder="Prix"
          value={saisie.prix}
          disabled={envoi}
          onChange={(e) => quandChamp('prix', e.target.value)}
        />
        <button className="bouton" type="submit" disabled={envoi || saisie.prix.trim() === ''}>
          Ajouter la ligne
        </button>
      </form>
      <p className="aide-liste">
        La borne basse est <b>incluse</b>, la haute <b>exclue</b> : « 1 → 2 »
        puis « 2 → 3 » se suivent sans trou ni recouvrement, et « 1 → 2 »
        s’affiche « 1 pal ». Laissez la borne haute vide pour « au-delà de »,
        et les deux vides pour une ligne qui vaut quel que soit le nombre.
        {grille.unite === 'kilometre' ? (
          <>
            {' '}
            Le prix est ici <b>au kilomètre</b> : il se multiplie par la
            distance.
          </>
        ) : (
          <>
            {' '}
            Le prix est le <b>total du transport</b> pour qui tombe dans la
            tranche — on ne le multiplie par rien.
          </>
        )}
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// L'ÉTAT
// ---------------------------------------------------------------------------

export function EcranTarifs({
  jeton,
  quandAccesPerdu,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}) {
  const [grilles, setGrilles] = useState<readonly Grille[]>([]);
  const [cloturees, setCloturees] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [ouverte, setOuverte] = useState<{
    readonly grille: Grille;
    readonly lignes: readonly LigneTarif[];
  } | null>(null);
  const [saisieGrille, setSaisieGrille] = useState<SaisieGrille | null>(null);
  const [saisieLigne, setSaisieLigne] = useState<SaisieLigne>(LIGNE_VIDE);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [clients, setClients] = useState<readonly LigneListe[]>([]);
  const [sites, setSites] = useState<readonly LigneListe[]>([]);
  const [departements, setDepartements] = useState<readonly Departement[]>([]);
  const [vehicules, setVehicules] = useState<readonly Vehicule[]>([]);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * LA PHRASE DE L'API PASSE AVANT LA MIENNE — 15/09/2026.
   *
   * Constaté par Mohamed devant la fenêtre de création : « La saisie est
   * incomplète ou hors des valeurs attendues ». Vrai, inutile, et incapable de
   * dire QUEL champ sur les neuf. L'API, elle, disait « du : obligatoire » ou
   * « l'indexation se pose entière : un indice de référence ET une part
   * gasoil » — et `echec()` jetait cette phrase.
   *
   * Mes phrases restent, comme REPLI : une panne de réseau n'a pas de message
   * d'API, et un accès perdu se dit mieux en « reconnectez-vous » qu'en
   * « jeton refusé ».
   */
  const finir = (resultat: {
    etat: 'ok' | 'echec';
    cause?: EchecApi;
    message?: string;
  }) => {
    setEnvoi(false);
    setRefus(
      resultat.etat === 'ok' || resultat.cause === undefined
        ? null
        : (resultat.message ?? REFUS[resultat.cause] ?? REFUS_INCONNU),
    );
    if (resultat.etat === 'echec' && resultat.cause === 'acces') quandAccesPerdu();
  };

  // Les référentiels, une fois : ils ne bougent pas pendant qu'on saisit un
  // tarif.
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const [c, s, d, v] = await Promise.all([
        lireListe(j, ANNUAIRE_CLIENTS),
        lireListe(j, ANNUAIRE_SITES),
        lireDepartements(j),
        lireVehicules(j),
      ]);
      if (abandonne) return;
      if (c.etat === 'ok') setClients(c.valeur);
      if (s.etat === 'ok') setSites(s.valeur);
      if (d.etat === 'ok') setDepartements(d.valeur);
      if (v.etat === 'ok') setVehicules(v.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  const relireLaListe = useCallback(
    async (fermees: boolean) => {
      setChargement(true);
      const j = await avecJeton();
      if (j === null) return;
      const lues = await lireLesGrilles(j, fermees);
      if (lues.etat === 'ok') {
        setGrilles(lues.valeur);
        setRefus(null);
      } else {
        // UNE LECTURE QUI ÉCHOUE NE SE TAIT PLUS — 15/09/2026. Elle se taisait,
        // et l'écran affichait « 0 grille active » : exactement ce qu'il aurait
        // affiché si tout allait bien. Une panne et un dépôt vide se
        // ressemblaient trait pour trait.
        setRefus(lues.message ?? REFUS[lues.cause] ?? REFUS_INCONNU);
        if (lues.cause === 'acces') quandAccesPerdu();
      }
      setChargement(false);
    },
    [avecJeton, quandAccesPerdu],
  );

  useEffect(() => {
    if (ouverte === null) void relireLaListe(cloturees);
  }, [relireLaListe, cloturees, ouverte]);

  const ouvrir = (id: string) => {
    setRefus(null);
    setMessage(null);
    setSaisieLigne(LIGNE_VIDE);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const lue = await lireLaGrille(j, id);
      if (lue.etat === 'ok') setOuverte(lue.valeur);
      else finir(lue);
    })();
  };

  const ajouterLaLigne = () => {
    if (ouverte === null) return;
    const g = ouverte.grille;
    const s = saisieLigne;

    const faute = fauteDeMontant(s.prix, 'Prix');
    if (faute !== null) {
      setRefus(faute);
      return;
    }

    setEnvoi(true);
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await ajouterUneLigneTarif(j, g.id, {
        ...(g.style_destination === 'site'
          ? s.destinationSiteId === ''
            ? {}
            : { destination_site_id: s.destinationSiteId }
          : s.destinationDepartement === ''
            ? {}
            : { destination_departement: s.destinationDepartement }),
        sens: s.sens,
        ...(s.vehiculeCode === '' ? {} : { vehicule_code: s.vehiculeCode }),
        prix: enNombre(s.prix),
        // LES BORNES NE PARTENT QUE DANS L'UNITÉ QUI LES PORTE : la base les
        // refuserait ailleurs, et les envoyer quand même donnerait un refus
        // que l'utilisateur ne comprendrait pas.
        ...(g.unite === 'trajet'
          ? {
              ...(enEntier(s.min) === null ? {} : { poids_min_kg: enEntier(s.min) }),
              ...(enEntier(s.max) === null ? {} : { poids_max_kg: enEntier(s.max) }),
            }
          : {}),
        ...(g.unite === 'kilometre'
          ? {
              ...(enEntier(s.min) === null ? {} : { km_min: enEntier(s.min) }),
              ...(enEntier(s.max) === null ? {} : { km_max: enEntier(s.max) }),
            }
          : {}),
        ...(g.unite === 'palette'
          ? {
              ...(enEntier(s.min) === null ? {} : { palettes_min: enEntier(s.min) }),
              ...(enEntier(s.max) === null ? {} : { palettes_max: enEntier(s.max) }),
            }
          : {}),
      });
      if (resultat.etat === 'ok') {
        setOuverte(resultat.valeur);
        // LA DESTINATION ET LA TRANCHE SE VIDENT, LE SENS ET LE VÉHICULE NON :
        // on saisit douze destinations d'affilée avec le même sens.
        setSaisieLigne((a) => ({
          ...a,
          destinationSiteId: '',
          destinationDepartement: '',
          prix: '',
          min: '',
          max: '',
        }));
      }
      finir(resultat);
    })();
  };

  const retirerLaLigne = (ligneId: string) => {
    if (ouverte === null) return;
    const grilleId = ouverte.grille.id;
    setEnvoi(true);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await retirerUneLigneTarif(j, grilleId, ligneId);
      if (resultat.etat === 'ok') setOuverte(resultat.valeur);
      finir(resultat);
    })();
  };

  const cloturer = () => {
    if (ouverte === null) return;
    const grilleId = ouverte.grille.id;
    setEnvoi(true);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await modifierUneGrille(j, grilleId, {
        au: new Date().toISOString().slice(0, 10),
      });
      if (resultat.etat === 'ok') {
        setOuverte(resultat.valeur);
        setMessage(
          'Grille clôturée. Elle reste lisible, et les ordres déjà tarifés ne changent pas.',
        );
      }
      finir(resultat);
    })();
  };

  const dupliquer = () => {
    if (ouverte === null) return;
    const grilleId = ouverte.grille.id;
    setEnvoi(true);
    setMessage(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await dupliquerUneGrille(j, grilleId, {});
      if (resultat.etat === 'ok') {
        setOuverte({ grille: resultat.valeur.grille, lignes: resultat.valeur.lignes });
        setMessage(
          `Copie ouverte — ${resultat.valeur.recopiees} ligne(s) recopiée(s) sur ${resultat.valeur.sur}.`,
        );
      }
      finir(resultat);
    })();
  };

  const enregistrerLaGrille = () => {
    if (saisieGrille === null) return;
    const s = saisieGrille;

    // ON REFUSE ICI CE QU'ON SAIT REFUSER, avec l'étiquette que l'utilisateur
    // lit à l'écran plutôt qu'un nom de colonne.
    const faute =
      fauteDeMontant(s.indice, 'Indice de référence', 4) ??
      fauteDeMontant(s.part, 'Part gasoil');
    if (faute !== null) {
      setRefus(faute);
      return;
    }

    setEnvoi(true);
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await creerUneGrille(j, {
        client_id: s.clientId,
        nom: s.nom.trim(),
        unite: s.unite,
        style_origine: s.styleOrigine,
        style_destination: s.styleDestination,
        ...(s.styleOrigine === 'site'
          ? s.origineSiteId === ''
            ? {}
            : { origine_site_id: s.origineSiteId }
          : s.origineDepartement === ''
            ? {}
            : { origine_departement: s.origineDepartement }),
        du: s.du,
        // LES DEUX PARTENT DÈS QUE L'UN EST REMPLI — 15/09/2026.
        //
        // La première rédaction ne les envoyait que si l'INDICE était rempli :
        // une part gasoil saisie seule disparaissait EN SILENCE, et la grille
        // naissait sans clause sans que rien ne le dise. Désormais l'API voit
        // la moitié de clause et explique laquelle manque.
        ...(s.indice.trim() === '' && s.part.trim() === ''
          ? {}
          : { indice_reference: enNombre(s.indice), part_gazole: enNombre(s.part) }),
      });
      if (resultat.etat === 'ok') {
        setSaisieGrille(null);
        setOuverte(resultat.valeur);
      }
      finir(resultat);
    })();
  };

  return (
    <div className="corps corps-ot">
      <section className="liste">
        {saisieGrille !== null && (
          <BoiteGrille
            saisie={saisieGrille}
            clients={clients}
            sites={sites}
            departements={departements}
            envoi={envoi}
            refus={refus}
            quandChamp={(cle, valeur) =>
              setSaisieGrille((a) => (a === null ? a : { ...a, [cle]: valeur }))
            }
            quandFermer={() => {
              setSaisieGrille(null);
              setRefus(null);
            }}
            quandEnregistrer={enregistrerLaGrille}
          />
        )}

        {ouverte === null ? (
          <>
            {refus !== null && <p className="bandeau-refus">{refus}</p>}
            <VueGrilles
              grilles={grilles}
              cloturees={cloturees}
              chargement={chargement}
              quandCloturees={setCloturees}
              quandOuvrir={ouvrir}
              quandNouvelle={() => {
                setRefus(null);
                setSaisieGrille(SAISIE_VIDE);
              }}
            />
          </>
        ) : (
          <VueGrille
            grille={ouverte.grille}
            lignes={ouverte.lignes}
            saisie={saisieLigne}
            sites={sites}
            departements={departements}
            vehicules={vehicules}
            envoi={envoi}
            refus={refus}
            message={message}
            quandRetour={() => {
              setOuverte(null);
              setRefus(null);
              setMessage(null);
            }}
            quandChamp={(cle, valeur) => setSaisieLigne((a) => ({ ...a, [cle]: valeur }))}
            quandAjouter={ajouterLaLigne}
            quandRetirer={retirerLaLigne}
            quandCloturer={cloturer}
            quandDupliquer={dupliquer}
          />
        )}
      </section>
    </div>
  );
}

export { supprimerUneGrille };
